import React, { useCallback, useEffect, useRef } from 'react'
import { batch, useSignal } from '@preact/signals-react'
import {
	Box, Table, TableBody, TableCell,
	TableHead, TableRow, Typography,
} from '@mui/material'
import { getSubmit } from '@ui-seeker/utils'
import { BACKEND_APIS } from '@constants'
import { AppButtonIcon, AppModal, AppRetailerBlock, AppTableSkeleton } from '@components'
import { dsColorNew } from '@palette'
import { runningSearchesOpen } from 'Pages/Admin/GlobalExecution/@signals'

const POLL_INTERVAL = 300000

function useRunningSearches() {
	const data = useSignal([])
	const isLoading = useSignal(true)
	const selectedSiteName = useSignal(null)
	const isMounted = useRef(true)
	const timeoutId = useRef(null)

	const poll = useCallback(() => {
		getSubmit(`${BACKEND_APIS.MASTER_DATA}/admin/options/running-searches-per-site-user`)
			.then(res => {
				if (isMounted.current) {
					batch(() => {
						data.value = res ?? []
						isLoading.value = false
					})
				}
			})
			.catch(() => { if (isMounted.current) isLoading.value = false })

		if (isMounted.current) {
			timeoutId.current = setTimeout(poll, POLL_INTERVAL)
		}
	}, [])

	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
			clearTimeout(timeoutId.current)
		}
	}, [])

	useEffect(() => {
		if (!runningSearchesOpen.value) {
			clearTimeout(timeoutId.current)
			batch(() => {
				data.value = []
				selectedSiteName.value = null
			})
			return
		}
		isLoading.value = true
		poll()
		return () => clearTimeout(timeoutId.current)
	}, [runningSearchesOpen.value, poll])

	const selectedSite = selectedSiteName.value
		? (data.value.find(r => r.siteName === selectedSiteName.value) ?? null)
		: null

	return {
		data: data.value,
		isLoading: isLoading.value,
		selectedSite,
		selectSite: (row) => { selectedSiteName.value = row?.siteName ?? null },
		clearSite: () => { selectedSiteName.value = null },
	}
}

export function RunningSearchesInfoButton() {
	return (
		<AppButtonIcon icon="circle-info" tooltip="Running searches per site" onClick={() => { runningSearchesOpen.value = true }}/>
	)
}

const cellSx = { '& .MuiTableBody-root .MuiTableCell-root': { color: dsColorNew.textDark } }
const boldCell = { fontWeight: 'bold' }

export default function RunningSearchesModal() {
	const { data, isLoading, selectedSite, selectSite, clearSite } = useRunningSearches()

	const onClose = () => { runningSearchesOpen.value = false }

	return (
		<AppModal
			icon="magnifying-glass-chart"
			title={selectedSite ? `${selectedSite.siteName} — Searches per user` : 'Running searches per site'}
			isOpen={runningSearchesOpen.value}
			controller={onClose}
			hideActions
		>
			{isLoading ? (
				<AppTableSkeleton rows={4} columns={3}/>
			) : selectedSite ? (
				<>
					<Box sx={{ mb: 1 }}>
						<AppButtonIcon icon="arrow-left" onClick={clearSite}/>
					</Box>
					{selectedSite.users.length === 0 ? (
						<Typography color={dsColorNew.textMuted} textAlign="center" py={2}>No data</Typography>
					) : (
						<Box sx={{ overflowX: 'auto' }}>
							<Table size="small" sx={cellSx}>
								<TableHead>
									<TableRow>
										<TableCell sx={boldCell}>User</TableCell>
										<TableCell sx={boldCell} align="right">Searches</TableCell>
										<TableCell sx={boldCell} align="right">Done</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{selectedSite.users.map((row, i) => (
										<TableRow key={i} hover>
											<TableCell>{row.userName}</TableCell>
											<TableCell align="right">{row.searches}</TableCell>
											<TableCell align="right">{row.searchesDone ?? '—'}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					)}
				</>
			) : data.length === 0 ? (
				<Typography color={dsColorNew.textMuted} textAlign="center" py={2}>No running searches</Typography>
			) : (
				<Box sx={{ overflowX: 'auto' }}>
					<Table size="small" sx={cellSx}>
						<TableHead>
							<TableRow>
								<TableCell sx={boldCell}>Site</TableCell>
								<TableCell sx={boldCell} align="right">Searches</TableCell>
								<TableCell sx={boldCell} align="right">Done</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map((row, i) => (
								<TableRow key={i} hover>
									<TableCell>
										<AppRetailerBlock
											retailer={row.siteName}
											country={row.market}
											icon={row.icon}
										/>
									</TableCell>
									<TableCell align="right">{row.searches}</TableCell>
									<TableCell align="right">{row.searchesDone ?? '—'}</TableCell>
									<TableCell align="right" sx={{ width: 40, px: 0.5 }}>
										<AppButtonIcon icon="users" tooltip="Show users" onClick={() => selectSite(row)}/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
		</AppModal>
	)
}